import React, { useState } from "react";
import { useLocation, useOutletContext } from "react-router";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useFormContext } from "react-hook-form";
import { useMutation } from "urql";

import { getLangCode, language, recaptchaSiteKey, userRoles } from "../../config";
import { metaDefault } from "../../config/meta";
import { SIGNUP_MUTATION } from "../../graphql/mutations";
import OptionallyDisplayed from "../global/optionallyDisplayed";
import LoadingAnim from "../global/loadingAnim";
import ErrorMessage from "../global/errorMessage";
import { getString, handlePostRequest } from "../../utilities";
import AccountFields from "./accountFields";
import PaymentFields from "./paymentFields";
import StudentFields from "../global/studentFields";
import ClassroomPlans from "./classroomPlans";
import { submitError, subscribeSubmit } from "../../utilities/subscribe";
import { useLazyQuery } from "../../hooks";
import { RECAPTCHA_VERIFY_QUERY } from "../../graphql/queries";
import { mpPaymentSubmitted } from "../../utilities/mixpanel";

export default function ClassroomForm() {
  const [plan, setPlan] = useState("m12");
  const [submitErrors, setSubmitErrors] = useState(null);
  const [creditComplete, setCreditComplete] = useState(false);

  const { success, classroomData, submitting, setSubmitting, dataFetching, dataError } = useOutletContext();

  const {
    getValues,
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  const location = useLocation();

  const stripe = useStripe();
  const elements = useElements();

  const getVariables = () => {
    const { email, emailVerify, password, passwordVerify, accessName, accessCode } = getValues();

    const myVariables = {
      email: email.trim(),
      emailVerify: emailVerify.trim(),
      password,
      passwordVerify,
      billingPlan: classroomData[plan].planId,
      accessName,
      accessCode,
    };

    return myVariables;
  };

  const planData = classroomData?.[plan];

  const finalAmount = planData?.amount;

  const action = "Classroom_Form_Submission";

  const onSignupCompleted = (data) => {
    const cardElement = elements.getElement(CardElement);

    if (cardElement) {
      cardElement.clear();
    }

    success(
      data,
      { planId: planData?.planId, amount: planData?.amount, name: planData?.name },
      { invoiceId: data?.signup?.invoiceId },
    );
    setSubmitErrors(null);
  };

  const onSignupError = (error) => {
    setSubmitting(false);
    setSubmitErrors(error);
  };

  const [result, signup] = useMutation(SIGNUP_MUTATION);
  const { fetching: loading } = result;

  const onRecaptchaCompleted = async (data) => {
    const myVariables = getVariables();
    const cardElement = elements.getElement(CardElement);
    await subscribeSubmit({
      data,
      setSubmitErrors,
      cardElement,
      mutation: (variables) =>
        signup(variables).then((result) => handlePostRequest(result, onSignupCompleted, onSignupError)),
      creditComplete,
      action,
      stripe,
      myVariables,
      plan: { planId: planData?.planId, amount: planData?.amount, name: planData?.name },
      setSubmitting,
    });
  };

  const onRecaptchaError = (error) => {
    console.log("recaptcha error: ", error);
    submitError(error, setSubmitErrors, null);
  };

  const { executeQuery: recaptchaVerify, loading: recaptchaLoading } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onRecaptchaCompleted,
    onRecaptchaError,
  );

  const disabled = submitting || !classroomData || dataFetching || dataError || loading || recaptchaLoading;

  return (
    <>
      {metaDefault({
        path: location.pathname,
        title: `${getString("join.3")} • ${getString("subscribe.plans.classroom.title.2")}`,
      })}

      <ClassroomPlans plan={plan} setPlan={setPlan} planData={classroomData} />

      <form
        disabled={disabled}
        onSubmit={handleSubmit(() => {
          if (!disabled) {
            setSubmitting(true);

            // Mixpanel Event
            mpPaymentSubmitted({
              months: plan,
              planGroup: userRoles.classroom,
              amount: planData?.amount,
              finalAmount,
              pathname: location.pathname,
            });

            window.grecaptcha.enterprise.ready(async () => {
              const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });

              if (token) {
                recaptchaVerify({ token, action, verify: true, siteKey: recaptchaSiteKey });
              }
            });
          }
        })}
      >
        <div className="wrapper padded">
          <div className="container-small">
            <OptionallyDisplayed doDisplay={!!submitErrors}>
              <ErrorMessage error={submitErrors} />
            </OptionallyDisplayed>

            <h2>{getString("subscribe.create")}</h2>

            <AccountFields
              errors={errors}
              submitErrors={submitErrors}
              setSubmitErrors={setSubmitErrors}
              isDisabled={disabled}
              isClassroom={true}
            />
            <StudentFields submitErrors={submitErrors} setSubmitErrors={setSubmitErrors} isDisabled={disabled} />
            <PaymentFields
              // errors={errors}
              setCreditComplete={setCreditComplete}
              submitErrors={submitErrors}
              setSubmitErrors={setSubmitErrors}
              isNewSubscriber
              isDisabled={disabled}
            />

            <button type="submit" className="button-flat-color pt-green" disabled={disabled}>
              {getString("subscribe.submit")}
            </button>

            <div className="subscribe-terms">
              <p>
                {getString("subscribe.terms", {
                  replace: [
                    getLangCode() === language.la ? getString("subscribe.title.1") : getString("subscribe.title.0"),
                  ],
                  html: true,
                })}
              </p>
            </div>
          </div>
        </div>
      </form>

      {(loading || recaptchaLoading || submitting) && (
        <LoadingAnim position="fixed" className="background-transparent-white">
          <h4>{getString("subscribe.loading")}</h4>
        </LoadingAnim>
      )}
    </>
  );
}
